"use client";

import { useState, useRef, useEffect } from "react";
import { FiSearch, FiChevronDown } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslations, useLocale } from "next-intl";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { regions, properties } from "@/data/main-data";

type OptionKeys = "type" | "location" | "price" | "area";

export default function HeroSearch() {
  const t = useTranslations("Home");
  const locale: any = useLocale();
  const router = useRouter();

  const [tab, setTab] = useState<"buy" | "rent">("buy");
  const [openDropdown, setOpenDropdown] = useState<OptionKeys | null>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ---- Proper (value + label) options ----
  const options = {
    type: properties.map((i) => ({
      value: i.value,
      label: locale === "ar" ? i.label_ar : i.label_en,
    })),

    location: regions.map((i) => ({
      value: i.value,
      label: locale === "ar" ? i.label_ar : i.label_en,
    })),

    price: [
      { value: "100000", label: "100,000" },
      { value: "200000", label: "200,000" },
      { value: "500000", label: "500,000" },
      { value: "1000000", label: "1,000,000" },
      { value: "10000000", label: "10,000,000" },
      { value: "50000000", label: "50,000,000" },
    ],

    area: [100, 200, 300, 400, 500, 600, 700, 800, 900, 1000].map((num) => ({
      value: String(num),
      label: `${num} ${locale === "ar" ? "م²" : "m²"}`,
    })),
  };

  const [selected, setSelected] = useState({
    type: properties[0].value,
    location: regions[0].value,
    price: "100000",
    area: "100",
  });

  const handleSelect = (key: OptionKeys, value: string) => {
    setSelected((prev) => ({ ...prev, [key]: value }));
    setOpenDropdown(null);
  };

  const handleSearch = () => {
    const query = new URLSearchParams({
      type: [selected.type].toString(),
      location: [selected.location].toString(),
      price: selected.price,
      area: selected.area,
      tab: tab,
    }).toString();

    router.push(`/${locale}/properties?${query}`);
  };

  const mainSearchOptions = [
    { key: "type", label: t("type"), icon: FiSearch },
    { key: "location", label: t("location"), icon: FiSearch },
    { key: "area", label: t("area"), icon: FiSearch },
  ];

  return (
    <div className="relative bottom-15 max-w-350 w-full mx-auto flex justify-center px-4 z-10 flex-wrap">
      <div className="bg-white/90 backdrop-blur-md shadow-2xl border border-gray-200 rounded-2xl p-6 md:p-8 w-full md:w-[85%]">
        {/* TABS */}
        <div className="flex gap-4 mb-6">
          {["buy", "rent"].map((item) => (
            <button
              key={item}
              onClick={() => setTab(item as "buy" | "rent")}
              className={`px-4 py-2 rounded-full font-semibold transition uppercase text-sm cursor-pointer ${
                tab === item
                  ? "bg-main text-white shadow-md scale-105"
                  : "bg-gray-100 hover:bg-gray-200"
              }`}
            >
              {item === "buy" ? t("buy") : t("rent")}
            </button>
          ))}
        </div>

        {/* FILTERS */}
        <div
          ref={dropdownRef}
          className="grid xs:grid-cols-2 lg:grid-cols-4 gap-4"
        >
          {mainSearchOptions.map((e) => (
            <div key={e.key} className="relative">
              <button
                type="button"
                onClick={() =>
                  setOpenDropdown(
                    openDropdown === e.key ? null : (e.key as OptionKeys)
                  )
                }
                className="w-full flex items-center justify-between border rounded-xl p-3 text-sm bg-gray-50 hover:bg-gray-100 focus:ring-2 focus:ring-main focus:border-main transition"
              >
                <div className="flex items-center gap-2 text-gray-700">
                  <e.icon className="text-gray-500 text-lg" />

                  {/* Show label based on selected value */}
                  <span>
                    {
                      options[e.key as OptionKeys].find(
                        (i) => i.value === selected[e.key as OptionKeys]
                      )?.label
                    }
                  </span>

                  {e.key === "price" && (
                    <Image src={"/sar.png"} alt="SAR" width={16} height={16} />
                  )}
                </div>

                <FiChevronDown
                  className={`transition-transform ${
                    openDropdown === e.key ? "rotate-180" : ""
                  }`}
                />
              </button>

              {/* Dropdown */}
              <AnimatePresence>
                {openDropdown === e.key && (
                  <motion.ul
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute left-0 right-0 mt-2 bg-white border rounded-xl shadow-lg overflow-hidden z-20 max-h-64 overflow-y-auto"
                  >
                    {options[e.key as OptionKeys].map((option) => (
                      <li
                        key={option.value}
                        onClick={() =>
                          handleSelect(e.key as OptionKeys, option.value)
                        }
                        className="px-4 py-2 hover:bg-main hover:text-white text-sm cursor-pointer transition flex items-center gap-1"
                      >
                        {option.label}

                        {e.key === "price" && (
                          <Image
                            src={"/sar.png"}
                            alt="SAR"
                            width={12}
                            height={12}
                          />
                        )}
                      </li>
                    ))}
                  </motion.ul>
                )}
              </AnimatePresence>
            </div>
          ))}

          {/* Search Button */}
          <button
            onClick={handleSearch}
            className="flex items-center justify-center gap-2 bg-main hover:bg-main-dark text-white rounded-xl font-semibold p-3 transition-all shadow-md hover:shadow-lg active:scale-95 cursor-pointer xs:col-span-2 lg:col-span-1"
          >
            <FiSearch className="text-xl" />
            {t("exploreButton")}
          </button>
        </div>
      </div>
    </div>
  );
}
